import React, { useRef, useEffect } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { useDataStore } from 'files/StateManagement.js';

import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import LottieView from 'views/Lottie.js';
import Utils from 'files/Utils.js';

const Panel = ({ children, className, column, index, name, options = {}, style }) => {

    const { maxWidth, removeOverflow, removePadding, shouldStyle, subTitle } = options || {};

    const [animations, setAnimations] = useSpring(() => ({
        opacity: 0,
        top: -50,
        config: { mass: 1, tension: 180, friction: 12 }
    }));
    const contentContainer = useRef(null);

    const runAnimations = async () => {
        try {
            await Utils.sleep(0.2 * index);
            setAnimations({
                top: 0,
                opacity: 1
            })
        } catch(e) {
            console.error(e.message);
        }
    }

    useEffect(() => {
        runAnimations();
    }, []);

    return (
        <animated.div
        className={`panel window ${className || `${column || 'col-12'} p-2`}`}
        style={{
            marginBottom: 24,
            position: 'relative',
            ...animations
        }}>
            <div style={{
                maxWidth: maxWidth,
                position: 'relative',
                width: '100%',
                ...style
            }}>
                <div style={{
                    ...shouldStyle !== false && Appearance.styles.panel(),
                    height: '100%'
                }}>
                    <div style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        flexGrow: 1
                    }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            padding: 8
                        }}>
                            <span style={{
                                color: Appearance.colors.text(),
                                fontSize: 18,
                                fontWeight: 800,
                                lineHeight: 1.5,
                                marginBottom: 2,
                                maxWidth: '100%',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>{name}</span>
                            <span style={{
                                color: Appearance.colors.subText(),
                                fontSize: 14,
                                fontWeight: 600,
                                lineHeight: 1.5,
                                maxWidth: '100%',
                                whiteSpace: 'normal'
                            }}>{subTitle}</span>
                        </div>
                    </div>
                    <div
                    className={'card-body p-0'}
                    style={{
                        position: 'relative'
                    }}>
                        <div
                        ref={contentContainer}
                        className={`card-body-content`}
                        style={{
                            padding: removePadding ? 0 : 15,
                            overflowY: removeOverflow === true ? 'visible' : 'scroll'
                        }}>
                            {children}
                        </div>
                    </div>
                </div>
            </div>
        </animated.div>
    )
}

export const PanelContentContainer = ({ body, bottomContent, buttons = [], children, childrenStyle, containerStyle, header, leftContent, loading, options = {}, rightContent, style, title, titleStyle }) => {

    const { buttonsPosition = 'top' } = options;
    const { system } = useDataStore();

    const getButtons = () => {

        let margins = buttonsPosition === 'top' ? 'mb-0 mx-0 mt-4 mt-xl-5' : '';
        let targets = (buttons || []).filter(button => button.visible !== false);
        return targets.length > 0 && (
            <div 
            className={`row ${margins} justify-content-center`}
            style={{
                maxWidth: leftContent || rightContent ? '100%' : targets.length === 1 ? 375 : 750,
                width: '100%'
            }}>
                {targets.map((button, index) => {
                    return (
                        <div 
                        className={`col-12 col-xl-${targets.length === 1 ? 12 : 6} px-0 pt-0 pb-${index === targets.length - 1 ? 0 : 2} px-xl-1 py-xl-0`}
                        key={index}>
                            <Button
                            {...button}
                            containerStyle={{
                                width: '100%'
                            }}
                            label={button.text}
                            type={'large'} />
                        </div>
                    )
                })}
            </div>
        )
    }

    const getContent = () => {

        if(leftContent) {
            return (
                <div 
                className={'row m-0'}
                style={{
                    width: '100%'
                }}>
                    <div className={'col-12 col-xl-5 p-0'}>
                        <div style={{
                            height: '100%',
                            overflow: 'hidden',
                            position: 'relative',
                            width: '100%',
                            ...Utils.isMobile() === false && {
                                clipPath: 'polygon(0% 0, 84% 0, 100% 100%, 0% 100%)'
                            }
                        }}>
                            {leftContent}
                        </div>
                    </div>
                    <div className={'col-12 col-xl-7 p-0 d-flex flex-column justify-content-center'}>
                        {getStructure()}
                    </div>
                </div>
            )
        }

        if(rightContent) {
            return (
                <div 
                className={'row m-0'}
                style={{
                    width: '100%'
                }}>
                    <div className={'col-12 col-xl-7 p-0 d-flex flex-column justify-content-center'}>
                        {getStructure()}
                    </div>
                    <div className={'col-12 col-xl-5 p-0'}>
                        <div style={{
                            height: '100%',
                            overflow: 'hidden',
                            position: 'relative',
                            width: '100%',
                            ...Utils.isMobile() === false && {
                                clipPath: 'polygon(16% 0, 100% 0, 100% 100%, 0% 100%)'
                            }
                        }}>
                            {rightContent}
                        </div>
                    </div>
                </div>
            )
        }

        return getStructure();
    }

    const getContentComponents = () => {
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: getPadding(),
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        return (
            <>
            {(children?.length > 0 || typeof(children) == 'object') && (
                <div style={{
                    paddingBottom: getPadding(),
                    paddingLeft: getPadding(),
                    paddingRight: getPadding(),
                    paddingTop: Utils.isMobile() === false ? 0 : getPadding(),
                    width: '100%',
                    ...childrenStyle
                }}>
                    {children}
                </div>
            )}
            {buttonsPosition === 'bottom' && (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: getPadding(),
                    width: '100%'
                }}>
                    {getButtons()}
                </div>
            )}
            </>
        )
    }

    const getPadding = () => {
        return system.window.isMobile ? 24 : Appearance.styles.panelPadding();
    }

    const getStructure = () => {

        let hasContent = children?.length > 0 || typeof(children) == 'object' || buttonsPosition === 'bottom' ? true : false;
        return (
            <>
            {header}
            <div style={{
                alignItems: leftContent ? 'flex-start' : (rightContent ? 'flex-end' : 'center'),
                borderBottom: Utils.isMobile() === true && hasContent === true ? `1px solid ${Appearance.colors.divider()}` : null,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: getPadding(),
                textAlign: leftContent || rightContent || Utils.isMobile() ? 'left' : 'center',
                width: '100%',
                ...containerStyle
            }}>
                <span style={{
                    ...Appearance.textStyles.panelHeaderText(),
                    lineHeight: 1.25,
                    marginBottom: 12,
                    width: '100%',
                    ...titleStyle
                }}>{title.toUpperCase()}</span>
                {typeof(body) === 'string' && (
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        whiteSpace: 'pre-wrap'
                    }}>{body}</span>
                )}
                {typeof(body) === 'function' && body({ textStyles: Appearance.textStyles.defaultText()})}
                {bottomContent}
                {buttonsPosition === 'top' && getButtons()}
            </div>
            {getContentComponents()}
            </>
        )
    }

    return (
        <div 
        className={'text-left text-xl-center'}
        style={{
            backgroundColor: Appearance.colors.panelBackground(),
            borderRadius: system.window.isMobile ? 0 : 10,
            maxWidth: 1250,
            minWidth: 0,
            overflow: 'hidden',
            width: '100%',
            ...style
        }}>
            {getContent()}
        </div>
    )
}

export default Panel;
